import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Home, BookOpen, Heart, Sparkles, ArrowRight, Calendar, User } from 'lucide-react';
import MarkdownRenderer from './MarkdownRenderer';

function WisdomLibrary() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    fetchEntries();
  }, []);

  const fetchEntries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lore/global`);
      if (response.ok) {
        const data = await response.json();
        setEntries(data.lore_entries || []);
      }
    } catch (error) {
      console.error('Failed to fetch wisdom entries');
    } finally {
      setLoading(false);
    }
  };

  if (selectedEntry) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#faf9f6' }}>
        {/* Header */}
        <header className="border-b border-gray-200 py-4 px-4 bg-white sticky top-0 z-10">
          <div className="max-w-4xl mx-auto flex justify-between items-center">
            <Button
              variant="ghost"
              onClick={() => setSelectedEntry(null)}
              className="text-gray-600 hover:text-gray-900"
            >
              ← Back to Library
            </Button>
            <div className="flex gap-2">
              <a href="/">
                <Button variant="outline" size="sm">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Button>
              </a>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <article className="max-w-3xl mx-auto py-12 px-4">
          <div className="mb-8">
            {selectedEntry.featured && (
              <Badge className="mb-4 bg-amber-100 text-amber-800 border-amber-300">
                <Sparkles className="w-3 h-3 mr-1" />
                Featured
              </Badge>
            )}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif', lineHeight: 1.2 }}>
              {selectedEntry.title}
            </h1>
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" />
                {selectedEntry.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(selectedEntry.created_at).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>

          <div className="prose prose-lg max-w-none" style={{ fontFamily: 'Georgia, serif' }}>
            <MarkdownRenderer content={selectedEntry.content} />
          </div>

          {/* Share / CTA */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-8 rounded-lg text-center">
              <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                Learn the Language of Belonging
              </h3>
              <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                Join The Belonging Sessions and discover how compassionate communication can transform your relationships and community.
              </p>
              <a href="/">
                <Button className="bg-black text-white hover:bg-gray-800 px-8 py-3">
                  Reserve Your Seat
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#faf9f6' }}>
      {/* Hero Header */}
      <header 
        className="relative py-24 px-4 text-center"
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
        }}
      >
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-white/10 rounded-full backdrop-blur-sm">
              <BookOpen className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Wisdom Library
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            Stories, insights, and practices from the Language of Belonging
          </p>
        </div>

        {/* Navigation */}
        <div className="absolute top-4 right-4 flex gap-2">
          <a href="/">
            <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
              <Home className="w-4 h-4 mr-2" />
              Home
            </Button>
          </a>
          <a href="/lore">
            <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
              <Heart className="w-4 h-4 mr-2" />
              Full LORE
            </Button>
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto py-16 px-4">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-64 bg-gray-200 rounded-lg"></div>
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20">
            <BookOpen className="w-20 h-20 mx-auto mb-6 text-gray-300" />
            <h2 className="text-2xl font-bold text-gray-600 mb-2">Coming Soon</h2>
            <p className="text-gray-500">We're gathering wisdom to share with you.</p>
          </div>
        ) : (
          <>
            {/* Featured Entry */}
            {entries.filter(e => e.featured)[0] && (
              <div className="mb-12">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">Featured Story</h2>
                <Card 
                  className="overflow-hidden cursor-pointer group hover:shadow-xl transition-all duration-300"
                  onClick={() => setSelectedEntry(entries.filter(e => e.featured)[0])}
                  style={{ borderRadius: '12px' }}
                >
                  <div className="md:flex">
                    <div 
                      className="md:w-1/3 h-48 md:h-auto"
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        minHeight: '200px'
                      }}
                    >
                      <div className="h-full flex items-center justify-center">
                        <Sparkles className="w-16 h-16 text-white/60" />
                      </div>
                    </div>
                    <div className="md:w-2/3 p-8">
                      <Badge className="mb-3 bg-amber-100 text-amber-800 border-amber-300">
                        <Sparkles className="w-3 h-3 mr-1" />
                        Featured
                      </Badge>
                      <h3 className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-blue-600 transition-colors" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {entries.filter(e => e.featured)[0].title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {entries.filter(e => e.featured)[0].content.replace(/[#*_]/g, '').substring(0, 200)}...
                      </p>
                      <div className="flex items-center text-sm text-gray-500">
                        <User className="w-4 h-4 mr-1" />
                        {entries.filter(e => e.featured)[0].author}
                        <span className="mx-2">•</span>
                        <Calendar className="w-4 h-4 mr-1" />
                        {new Date(entries.filter(e => e.featured)[0].created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* All Entries Grid */}
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">All Stories</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {entries.map((entry) => (
                <Card 
                  key={entry.id}
                  className="overflow-hidden cursor-pointer group hover:shadow-lg transition-all duration-300"
                  onClick={() => setSelectedEntry(entry)}
                  style={{ borderRadius: '12px' }}
                >
                  <div 
                    className="h-32"
                    style={{
                      background: entry.featured 
                        ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
                        : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                    }}
                  >
                    <div className="h-full flex items-center justify-center">
                      {entry.featured ? (
                        <Sparkles className="w-10 h-10 text-white/70" />
                      ) : (
                        <BookOpen className="w-10 h-10 text-white/70" />
                      )}
                    </div>
                  </div>
                  <CardContent className="p-5">
                    {entry.featured && (
                      <Badge className="mb-2 bg-amber-100 text-amber-800 border-amber-300 text-xs">
                        Featured
                      </Badge>
                    )}
                    <h3 className="text-lg font-bold mb-2 group-hover:text-blue-600 transition-colors line-clamp-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {entry.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                      {entry.content.replace(/[#*_]/g, '').substring(0, 100)}...
                    </p>
                    <div className="flex items-center text-xs text-gray-500">
                      <User className="w-3 h-3 mr-1" />
                      {entry.author}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}

        {/* CTA Section */}
        <div className="mt-20 text-center">
          <div className="inline-block p-12 rounded-2xl" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Ready to Begin Your Journey?
            </h2>
            <p className="text-white/70 mb-8 max-w-md mx-auto">
              Join The Belonging Sessions and learn the Language of Belonging with a community of practice.
            </p>
            <a href="/">
              <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 px-8">
                Reserve Your Seat
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200 text-center text-gray-500 text-sm">
        <p>© 2025 The Belonging Sessions • Defiance Springs • Building compassionate connection</p>
      </footer>
    </div>
  );
}

export default WisdomLibrary;
